import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';

const OnlineMeeting: React.FC = () => {
  const colors = getThemeColors('dark');

  const [isConnecting, setIsConnecting] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [isCameraEnabled, setIsCameraEnabled] = useState(true);
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(true);
  const [meetingData, setMeetingData] = useState<any>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    initializeMeeting();
  }, []);

  const initializeMeeting = async () => {
    try {
      setIsConnecting(true);
      setConnectionError(null);

      // Simulate connection process
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock successful connection
      setIsConnected(true);
      setIsConnecting(false);

      // Mock meeting data
      setMeetingData({
        title: 'Virtual Chama Meeting',
        meetingId: 'meeting-123',
        participants: 5,
      });

      // Mock participants
      setParticipants([
        { id: '1', name: 'John Doe', isMuted: false, hasVideo: true },
        { id: '2', name: 'Jane Smith', isMuted: true, hasVideo: false },
        { id: '3', name: 'Bob Johnson', isMuted: false, hasVideo: true },
      ]);

    } catch (error) {
      console.error('Failed to initialize meeting:', error);
      setConnectionError('Failed to connect to meeting');
      setIsConnecting(false);
    }
  };

  const handleToggleCamera = async () => {
    const newState = !isCameraEnabled;
    setIsCameraEnabled(newState);
    alert(`Camera ${newState ? 'enabled' : 'disabled'}`);
  };

  const handleToggleMicrophone = async () => {
    const newState = !isMicrophoneEnabled;
    setIsMicrophoneEnabled(newState);
    alert(`Microphone ${newState ? 'enabled' : 'disabled'}`);
  };

  const handleSwitchCamera = async () => {
    alert('Camera switched');
  };

  const handleEndCall = () => {
    const confirm = window.confirm('Are you sure you want to leave the meeting?');
    if (confirm) {
      alert('Meeting ended');
      // Navigate back
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#000000',
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: 80, // Space for tab bar
    },
    loadingContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      paddingLeft: spacing.xl,
      paddingRight: spacing.xl,
    },
    loadingText: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: '#ffffff',
      marginTop: spacing.lg,
      textAlign: 'center',
    },
    loadingSubtext: {
      fontSize: typography.fontSize.base,
      color: '#cccccc',
      marginTop: spacing.sm,
      textAlign: 'center',
    },
    previewBadge: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
      color: colors.primary,
      marginTop: spacing.sm,
      textAlign: 'center',
    },
    errorContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      paddingLeft: spacing.xl,
      paddingRight: spacing.xl,
    },
    errorText: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.error,
      textAlign: 'center',
      marginBottom: spacing.sm,
    },
    errorSubtext: {
      fontSize: typography.fontSize.base,
      color: '#cccccc',
      textAlign: 'center',
    },
    meetingContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },
    videoArea: {
      flex: 1,
      backgroundColor: '#1a1a1a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    videoPlaceholder: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#666666',
    },
    videoIcon: {
      fontSize: 80,
      marginBottom: spacing.lg,
      opacity: 0.5,
    },
    videoText: {
      fontSize: typography.fontSize.lg,
      textAlign: 'center',
    },
    participantsGrid: {
      position: 'absolute',
      top: spacing.lg,
      right: spacing.lg,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: spacing.sm,
      maxWidth: 300,
    },
    participantCard: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderRadius: borderRadius.md,
      padding: spacing.sm,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: 100,
    },
    participantAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: spacing.xs,
    },
    participantName: {
      fontSize: typography.fontSize.sm,
      color: '#ffffff',
      textAlign: 'center',
      fontWeight: typography.fontWeight.medium,
    },
    participantStatus: {
      display: 'flex',
      gap: spacing.xs,
      marginTop: spacing.xs,
    },
    statusIcon: {
      fontSize: 12,
      opacity: 0.7,
    },
    controlsBar: {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      padding: spacing.lg,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: spacing.lg,
    },
    controlButton: {
      width: 60,
      height: 60,
      borderRadius: 30,
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: 24,
    },
    cameraButton: {
      backgroundColor: isCameraEnabled ? '#4CAF50' : '#f44336',
    },
    micButton: {
      backgroundColor: isMicrophoneEnabled ? '#4CAF50' : '#f44336',
    },
    switchButton: {
      backgroundColor: '#2196F3',
    },
    endButton: {
      backgroundColor: '#f44336',
      width: 70,
      height: 70,
      borderRadius: 35,
    },
    meetingInfo: {
      position: 'absolute',
      top: spacing.lg,
      left: spacing.lg,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      maxWidth: 300,
    },
    meetingTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: '#ffffff',
      marginBottom: spacing.xs,
    },
    meetingStats: {
      fontSize: typography.fontSize.sm,
      color: '#cccccc',
    },
    debugOverlay: {
      position: 'absolute',
      top: 50,
      right: 10,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: spacing.sm,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.xs,
      color: '#ffffff',
      fontFamily: 'monospace',
    },
  };

  // Loading state
  if (isConnecting) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={{ fontSize: 60, color: colors.primary }}>📹</div>
          <div style={styles.loadingText}>
            {isPreview ? 'Loading preview...' : 'Connecting to meeting...'}
          </div>
          <div style={styles.loadingSubtext}>
            Virtual Chama Meeting
          </div>
          {isPreview && (
            <div style={styles.previewBadge}>
              PREVIEW MODE
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (connectionError) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={{ fontSize: 60, color: colors.error, marginBottom: spacing.lg }}>❌</div>
          <div style={styles.errorText}>
            Failed to connect to meeting
          </div>
          <div style={styles.errorSubtext}>
            {connectionError}
          </div>
        </div>
      </div>
    );
  }

  // Main meeting interface
  return (
    <div style={styles.container}>
      <div style={styles.meetingContainer}>
        {/* Video Area */}
        <div style={styles.videoArea}>
          <div style={styles.videoPlaceholder}>
            <div style={styles.videoIcon}>📹</div>
            <div style={styles.videoText}>
              Meeting in progress<br />
              Video call interface would be here
            </div>
          </div>

          {/* Meeting Info Overlay */}
          <div style={styles.meetingInfo}>
            <div style={styles.meetingTitle}>
              {meetingData?.title || 'Virtual Meeting'}
            </div>
            <div style={styles.meetingStats}>
              {participants.length} participants • {isCameraEnabled ? 'Camera ON' : 'Camera OFF'} • {isMicrophoneEnabled ? 'Mic ON' : 'Mic OFF'}
            </div>
          </div>

          {/* Participants Grid */}
          <div style={styles.participantsGrid}>
            {participants.slice(0, 4).map((participant) => (
              <div key={participant.id} style={styles.participantCard}>
                <div style={styles.participantAvatar}>
                  <span style={{ color: '#ffffff', fontSize: 16 }}>
                    {participant.name.split(' ').map((n: string) => n[0]).join('')}
                  </span>
                </div>
                <div style={styles.participantName}>
                  {participant.name}
                </div>
                <div style={styles.participantStatus}>
                  <span style={{ ...styles.statusIcon, color: participant.hasVideo ? '#4CAF50' : '#f44336' }}>
                    {participant.hasVideo ? '📹' : '📷'}
                  </span>
                  <span style={{ ...styles.statusIcon, color: participant.isMuted ? '#f44336' : '#4CAF50' }}>
                    {participant.isMuted ? '🔇' : '🎤'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Controls Bar */}
        <div style={styles.controlsBar}>
          <button
            style={{ ...styles.controlButton, ...styles.cameraButton }}
            onClick={handleToggleCamera}
            title={isCameraEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            {isCameraEnabled ? '📹' : '📷'}
          </button>

          <button
            style={{ ...styles.controlButton, ...styles.micButton }}
            onClick={handleToggleMicrophone}
            title={isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone'}
          >
            {isMicrophoneEnabled ? '🎤' : '🔇'}
          </button>

          <button
            style={{ ...styles.controlButton, ...styles.switchButton }}
            onClick={handleSwitchCamera}
            title="Switch camera"
          >
            🔄
          </button>

          <button
            style={{ ...styles.controlButton, ...styles.endButton }}
            onClick={handleEndCall}
            title="End meeting"
          >
            📞
          </button>
        </div>

        {/* Debug overlay for development */}
        {process.env.NODE_ENV === 'development' && (
          <div style={styles.debugOverlay}>
            Camera: {isCameraEnabled ? '✅' : '❌'} | Mic: {isMicrophoneEnabled ? '✅' : '❌'}<br />
            Participants: {participants.length} | Connected: {isConnected ? '✅' : '❌'}
          </div>
        )}
      </div>
    </div>
  );
};

// Mock variable for preview mode
const isPreview = false;

export default OnlineMeeting;